import { useCallback, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useMakeConversationCache,
  useConversationIdListFiltered,
  useConversationIdListValue,
  useIsConversationDescMode,
  useSetConversationIdListValue,
} from 'hooks/business/conversation';
import { useClearConversations } from 'hooks/business/lruConversation';
import { useCategoryAwareSort } from 'hooks/business/conversationsHooks/useCategoryFilterState';
import {
  getImportantTodoConversationList,
  advanceQueryConversationList,
  getImportantTodoType,
  ImportantTodoScope,
} from 'services/api/conversation.convList';
import { sbsQueryConversationList, SbsConversationListParams } from 'services/api/sbs';
import { uniq } from 'lodash-es';
import { useDeepCompareEffect, useMemoizedFn } from 'ahooks';
import {
  useGetImportantScopeValue,
  useImportantTodoValue,
  useSetImportantTodo,
} from 'views/Conversation/ConvList/FilterTodo';
import {
  useCategoryAwareFilterAdvance,
  useCategoryAwareStoreFilter,
} from './conversationsHooks/useCategoryFilterState';
import type { TodoFilterType } from './conversationsHooks/useCategoryFilterState';
import type { ConversationCategoryType } from './conversationsHooks/useConversationCategory';
import {
  buildConversationListQueryKey,
  getNextOffsetFromTalkInfo,
  mergeConversationPageItems,
  normalizeConversationPage,
} from './useConversations.helpers';
import { ApiQueryKey } from 'services/config/queryKey';

const pageSize = 30;

const SYNC_PAGE_SIZE = 50;
const SYNC_INTERVAL = 60 * 1000;

export interface UseConversationsOptions {
  sort?: 'asc' | 'desc' | 'waitAsc' | 'waitDesc';
  isConversationDescMode?: boolean;
  cacheConvIds?: string[];
  setCacheConvIds?: Dispatch<SetStateAction<string[]>>;
}

/** 获取会话列表, 更新到本地会话列表的状态缓存 */
export default function useConversations(
  category: ConversationCategoryType,
  todoFilter?: TodoFilterType,
  options?: UseConversationsOptions,
) {
  const queryClient = useQueryClient();
  const addConversationToMap = useMakeConversationCache('set');
  const clearConversations = useClearConversations();
  const setConversationIdList = useSetConversationIdListValue();
  const filteredIdList = useConversationIdListFiltered();
  const importantTodo = useImportantTodoValue();
  const setImportantTodo = useSetImportantTodo();
  const getImportantScopeValue = useGetImportantScopeValue();
  const categoryFilterAdvance = useCategoryAwareFilterAdvance(category);
  const categoryStoreFilter = useCategoryAwareStoreFilter(category);
  const categorySort = useCategoryAwareSort(category);
  const globalDescMode = useIsConversationDescMode();

  const conversationSort = options?.sort ?? categorySort;
  const isConversationDescMode = options?.isConversationDescMode ?? globalDescMode;
  const providedCacheConvIds = options?.cacheConvIds;
  const setCacheConvIds = options?.setCacheConvIds;
  const processedPageCountRef = useRef(0);

  const getImportantTodoState = async () => {
    if (importantTodo) {
      const { importantTodoTypes, scope } = importantTodo;
      return {
        todoTypes: importantTodoTypes,
        onlyMy: getImportantScopeValue(scope) === ImportantTodoScope.OnlyMy,
      };
    }
    const { todoTypeName, options: todoOptions, scope } = await getImportantTodoType();
    const importantTodoTypes = todoTypeName.map(({ type }) => type);
    setImportantTodo({
      importantTodoTypes,
      options: todoOptions,
      scope,
    });
    return {
      todoTypes: importantTodoTypes,
      onlyMy: getImportantScopeValue(scope) === ImportantTodoScope.OnlyMy,
    };
  };

  const fetchListFromTodo = useMemoizedFn(
    async (params: ApiConversation.ConversationListAdvanceParams) => {
      const { todoTypes, onlyMy } = await getImportantTodoState();
      const { hasNext, nextOffset, talkInfo } = await getImportantTodoConversationList({
        ...params,
        statusFilters: undefined,
        todoTypes,
        onlyMy,
        unprocessed: todoFilter !== 'all',
      });

      return normalizeConversationPage({
        items: talkInfo,
        nextOffset,
        hasMore: hasNext,
      });
    },
  );

  const queryConversationListParams = useMemo<
    Omit<ApiConversation.ConversationListAdvanceParams, 'dir' | 'pageSize' | 'offset'>
  >(() => {
    return {
      ...categoryFilterAdvance,
      region: categoryStoreFilter.region,
      platform: categoryStoreFilter.platform,
      follower: categoryStoreFilter.follower,
      tunnelAccountIds: categoryStoreFilter.storeList?.map((x) => x.tunnelAccountId),
    };
  }, [categoryStoreFilter, categoryFilterAdvance]);

  const dir = useMemo<0 | 1 | 2 | 3>(() => {
    if (!isConversationDescMode) {
      return 0;
    }

    if (conversationSort === 'asc') {
      return 0;
    }
    if (conversationSort === 'desc') {
      return 1;
    }
    if (conversationSort === 'waitAsc') {
      return 2;
    }
    return 3;
  }, [conversationSort, isConversationDescMode]);

  const queryKey = useMemo(
    () =>
      buildConversationListQueryKey({
        category,
        todoFilter,
        sort: conversationSort,
        filterAdvance: categoryFilterAdvance,
        storeFilter: categoryStoreFilter,
        isConversationDescMode: isConversationDescMode ?? false,
      }),
    [
      category,
      todoFilter,
      conversationSort,
      categoryFilterAdvance,
      categoryStoreFilter,
      isConversationDescMode,
    ],
  );

  const clearLocalConversationState = useMemoizedFn(() => {
    console.log('[clearLocalConversationState] clear local conversation state');
    processedPageCountRef.current = 0;
    syncCallCountRef.current = 0;
    clearConversations();
    setConversationIdList([]);
    if (isConversationDescMode) {
      setCacheConvIds?.([]);
    }
  });

  const renderIdList = useMemo(() => {
    if (!isConversationDescMode) {
      return filteredIdList;
    }

    const cacheConvIds = providedCacheConvIds ?? [];
    return cacheConvIds.filter((id) => filteredIdList.includes(id));
  }, [filteredIdList, isConversationDescMode, providedCacheConvIds]);

  const pushToConversationState = useMemoizedFn((items: Conversation.Conversation[]) => {
    if (items.length === 0) {
      return;
    }

    addConversationToMap(items);
    const ids = items.map((item) => item.conversationId);
    setConversationIdList((prev) => uniq([...prev, ...ids]));

    if (isConversationDescMode) {
      setCacheConvIds?.((prev) => uniq([...prev, ...ids]));
    }
  });

  const isTodo = useMemo(() => {
    return Boolean(queryConversationListParams.statusFilters?.includes('IMPORTANT_TODO'));
  }, [queryConversationListParams.statusFilters]);

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const offset = (pageParam as string | undefined) ?? '0';
      const baseParams: ApiConversation.ConversationListAdvanceParams = {
        ...queryConversationListParams,
        offset,
        pageSize,
        suTalkMode: category === 'all',
        dir,
      };

      if (isTodo) {
        return fetchListFromTodo(baseParams);
      }

      const [pageRes, remindRes] = await Promise.all([
        advanceQueryConversationList(baseParams),
        offset === '0'
          ? advanceQueryConversationList({
              ...baseParams,
              pageSize: 9999,
              remind: true,
            })
          : Promise.resolve({ talkInfo: [] }),
      ]);

      return normalizeConversationPage({
        items: mergeConversationPageItems(pageRes.talkInfo, remindRes.talkInfo),
        nextOffset: getNextOffsetFromTalkInfo(pageRes.talkInfo, dir),
        hasMore: pageRes.talkInfo.length >= pageSize,
      });
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore) {
        return;
      }
      return lastPage.nextOffset;
    },
    onSuccess: (data) => {
      const newPages = data.pages.slice(processedPageCountRef.current);
      processedPageCountRef.current = data.pages.length;

      const items = newPages.flatMap((page) => page.items);
      pushToConversationState(items);
    },
  });

  const isSyncMode = useMemo(() => {
    const disabled = isConversationDescMode || isTodo;
    return category === 'mine' && !disabled;
  }, [category, isConversationDescMode, isTodo]);

  const syncCallCountRef = useRef(0);

  /** 同步会话列表 */
  useQuery({
    queryKey: [ApiQueryKey.SyncConversationList],
    queryFn: async () => {
      syncCallCountRef.current += 1;
      // 首次挂载不执行，等第一个 SYNC_INTERVAL 延迟到达后再真正请求
      if (syncCallCountRef.current <= 1) {
        return [];
      }

      const res = await advanceQueryConversationList({
        ...queryConversationListParams,
        pageSize: SYNC_PAGE_SIZE,
        offset: '0',
      });
      const talkInfo = res?.talkInfo || [];

      pushToConversationState(talkInfo);

      return talkInfo;
    },
    enabled: isSyncMode,
    refetchInterval: SYNC_INTERVAL,
  });

  useDeepCompareEffect(() => {
    clearLocalConversationState();
  }, [queryKey]);

  const refresh = useMemoizedFn(async () => {
    console.log('refresh');
    clearLocalConversationState();
    await queryClient.resetQueries({
      queryKey,
      exact: true,
    });
  });

  return {
    renderIdList,
    hasMore: query.hasNextPage ?? false,
    isLoading: query.isFetching && !query.isFetchingNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refresh,
  };
}

export const useSbsConversations = () => {
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState('0');
  const addConversationToMap = useMakeConversationCache('set');
  const conversationIdListValue = useConversationIdListValue();
  const setConversationIdList = useSetConversationIdListValue();

  const fetchListSbs = useCallback(
    (params: SbsConversationListParams) => {
      return sbsQueryConversationList(params)
        .then((res) => {
          if (res.talkInfo.length < pageSize) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
          const len = res.talkInfo.length;
          if (len > 0) {
            setOffset((offset) => `${Number(offset) + 1}`);
          }

          return res.talkInfo;
        })
        .then((res) => {
          if (res.length > 0) {
            const len = conversationIdListValue.length;
            res.forEach((v, i) => {
              // eslint-disable-next-line no-param-reassign
              v.sbsIndex = len + i;
            });

            addConversationToMap(res);
            setConversationIdList((pre) => uniq([...pre, ...res.map((_) => _.conversationId)]));
          }
        });
    },
    [addConversationToMap, setConversationIdList, setOffset, conversationIdListValue.length],
  );

  return {
    hasMore,
    list: conversationIdListValue,
    fetchListSbs,
    pageSize,
    offset,
    setOffset,
    setHasMore,
    setConversationIdList,
  };
};
